import { useEffect, useState } from 'react';
import styles from './FileUploadChip.module.scss';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import { useFileUpload } from '@utils';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Text, Tooltip } from '@components';
import { TOOLTIP_POSITION } from '@types';

type TRes = {
	data: any;
	error: null;
}

type ExternalProgress = {
	u_id: string;
	percentage: number;
	is_start: boolean;
	is_success: boolean;
	is_error: boolean;
}

type FileUploadChipType = {
	name: string;
	removeFile: (id: string) => void;
	id: string;
	fileToUpload?: File;
	fileClick?: (id?: string) => void;
	downloadFile?: (id?: string) => void;
	uploadProps?: {
		url: string;
		headers?: { [key: string]: string };
		domain?: 'EOS' | 'ShipmentAPI' | 'AdminAPI' | 'Inertia';
		payloadFileKey?: string;
		onUploadSuccess?: (res: TRes) => void;
		onUploadFailure?: (e: any) => void;
	};
	progressInfo?: ExternalProgress[];
};

export const FileUploadChip = (props:FileUploadChipType) => {
	const {
		name,
		removeFile,
		id,
		fileToUpload,
		uploadProps,
		fileClick,
		downloadFile,
		progressInfo,
	} = props;

	const [internalProgress, setInternalProgress] = useState<number | null>(null);
	const onUploading = (percent: number | null) => setInternalProgress(percent);
	const { uploadFile, uploadComplete, inProgress } = useFileUpload({
		url: uploadProps?.url ?? '',
		headers: uploadProps?.headers ?? {},
		onUploading,
	});
	const [success, setSuccess] = useState(false);
	const [failure, setFailure] = useState(false);
	const external = progressInfo?.find((p) => p.u_id === id);
	const percent = external ? external.percentage : internalProgress ?? 0;

	const isSuccess = external ? external.is_success : success;
	const isError = external ? external.is_error : failure;
	const showProgress = external?.is_start;

	useEffect(() => {
		if (fileToUpload && uploadProps && uploadProps.url && !uploadComplete && !inProgress) {
			setSuccess(false);
			setFailure(false);
			uploadFile({
				file: fileToUpload,
				payloadFileKey: uploadProps.payloadFileKey ?? 'file',
				domain: uploadProps.domain ?? 'EOS'
			}).then((res) => {
				if (uploadProps && uploadProps.onUploadSuccess && res) uploadProps.onUploadSuccess(res);
				setSuccess(true);
				setFailure(false);
			}).catch((e) => {
				if (uploadProps && uploadProps.onUploadFailure) uploadProps.onUploadFailure(e);
				setSuccess(false);
				setFailure(true);
			});
		}
	}, [fileToUpload, uploadProps, uploadComplete, inProgress, uploadFile]);

	return (
		<div className={styles.container}>
			<UploadFileIcon className={styles.icon} onClick={() => fileClick?.(id)} />
			<div className={styles.text}>
				<Text text={name} />
			</div>
			{!isSuccess && !isError && showProgress &&(
				<div className={styles.progressBar}>
					<div className={styles.progress} style={{ width: `${percent}%` }} />
				</div>
			)}
			{isSuccess && <CheckCircleOutlineIcon style={{ color: 'var(--func-green-dark)' }} />}
			{isError && <ErrorOutlineIcon style={{ color: 'var(--func-red-default)' }} />}
			{downloadFile && (
				<Tooltip text={'Download'} position={TOOLTIP_POSITION.TOP}>
					<button className={styles.button} onClick={() => downloadFile(id)} aria-label={'Download File'}>
						<DownloadIcon className={styles.icon} />
					</button>
				</Tooltip>
			)}
			<button className={styles.button} onClick={() => removeFile(id)} aria-label={'Remove File'}>
				<ClearIcon className={styles.icon} />
			</button>
		</div>
	);
};
